import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Filter } from 'lucide-react'
import BlogPost from '../components/BlogPost'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Post, Category } from '../types'
import { api } from '../services/api'

const CategoryPage: React.FC = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>()
  const navigate = useNavigate()
  const [posts, setPosts] = useState<Post[]>([])
  const [category, setCategory] = useState<Category | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchCategoryData = async () => {
      if (!categorySlug) return

      try {
        setLoading(true)
        setError(null)

        const [categoryRes, postsRes] = await Promise.all([
          api.get(`/categories/${categorySlug}`),
          api.get(`/posts?category=${categorySlug}`)
        ])

        setCategory(categoryRes.data || null)
        setPosts(Array.isArray(postsRes.data) ? postsRes.data : [])
      } catch (err: any) {
        console.error('Error fetching category data:', err)
        setError(err.response?.data?.error || 'Failed to load category')
      } finally {
        setLoading(false)
      }
    }

    fetchCategoryData()
  }, [categorySlug])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-excel-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading category content...</p>
        </div>
      </div>
    )
  }

  if (error || !category) {
    // Automatically redirect to home page when category not found
    useEffect(() => {
      const timer = setTimeout(() => {
        navigate('/', { replace: true })
      }, 100)
      return () => clearTimeout(timer)
    }, [navigate])

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Redirecting to home page...</p>
        </div>
      </div>
    )
  }

  const categoryColorMap: Record<string, string> = {
    'excel-tutorials': 'excel',
    'tips-tricks': 'amber',
    'news-updates': 'blue',
    'advanced-techniques': 'purple',
    'business-solutions': 'teal'
  }

  const categoryColor = categoryColorMap[category.slug] || 'default'

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center mb-4">
            <Link to="/">
              <Button variant="ghost" className="mr-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <Badge variant={categoryColor as any} className="mb-2">
                {category.name}
              </Badge>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {category.name}
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl">
                {category.description}
              </p>
            </div>
            
            <div className="mt-4 md:mt-0 flex items-center">
              <span className="text-sm text-gray-500 mr-4">
                {posts.length} {posts.length === 1 ? 'article' : 'articles'}
              </span>
              <Link to="/blog">
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  View All Categories
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        {posts.length === 0 ? (
          <div className="text-center py-16">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              No articles yet
            </h2>
            <p className="text-gray-600 mb-8">
              Content for this category is coming soon. Check back later!
            </p>
            <Link to="/admin">
              <Button variant="excel">
                Create Content
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => (
              <BlogPost 
                key={post.id} 
                post={post} 
                category={category}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CategoryPage
